<template>
    <section class="address-lookup">
        <!-- Address section -->
        <div class="row no-gutters address-group bg-white border rounded px-3-OFF py-4-OFF" v-loading="loading">
            <!-- help icon -->
            <div
                v-if="isSearchOpen && !isAddressEmpty"
                class="mt-0 mr-0"
                :style="{
                    position: 'absolute',
                    top: `${0 + 8}px`,
                    right: `${15 + 8}px`,
                    zIndex: 100,
                }"
            >
                <!-- <a
                  class="el-icon-info"
                  href="#"
                  @click="handleInfoClick"
                  :style="{ fontSize: '18px' }"
                />-->
                <a :disabled="disabled" class="el-icon-close" href="#" @click="showSearch(false)"
                   :style="{ fontSize: '22px' }"/>
            </div>

            <!-- Address Geocode Search - Top Row -->
            <div
                :style="{ borderBottom: '1px solid #ddd' }"
                :class="[
                    'col-12 border-bottom-OFF mb-1 bg-light px-3 py-3',
                    isSearchOpen ? '' : 'd-flex justify-content-between align-items-center',
                ]"
            >
                <!-- <label :for="`address${id}`"> -->
                <label :for="`address`">
                    <span class="mb-0">{{ isSearchOpen ? "Search " : "" }}{{ title }}</span>
                    <small v-if="addressId" class="px-1">
                        <em>{{ "ID: " + addressId }}</em>
                    </small>
                    <span
                        v-if="$store.getters['summary/applicationType'] == '2'"
                        class="badge badge-info"
                        v-on:click="$emit('copyToBusiness', addressComponents)"
                    >
                        Copy to business address
                    </span>
                </label>

                <!-- Google places geocoder -->
                <section
                    v-if="isSearchOpen"
                    :class="[
                        'mt-1',
                        'address-lookup__search-container',
                        isSearchOpen ? 'address-lookup__search-container--open' : 'address-lookup__search-container--closed',
                    ]"
                >
                    <place-auto-complete
                        v-model="autoCompleteValue"
                        :api-key="global('tenant.google_maps_api_key')"
                        :bounds="{ north: -10.6681857235, south: -43.6345972634, east: 153.569469029, west: 113.338953078 }"
                        placeholder="Find an address"
                        :autocomplete="false"
                        v-place-autofill:formatted_address="addressComponents.formatted_address"
                        v-place-autofill:subpremise="addressComponents.unit_number"
                        v-place-autofill:street_number="addressComponents.street_number"
                        v-place-autofill:route.short="addressComponents.route"
                        v-place-autofill:city="addressComponents.locality"
                        v-place-autofill:state.short="addressComponents.administrative_area_level_1"
                        v-place-autofill:zipcode="addressComponents.postal_code"
                        v-place-autofill:country.short="addressComponents.country"
                        :disabled="isRefAppSubmitted || disabled"
                        :showActivityIndicator="true"
                        @autocomplete-select="handleGoogleAddressChange"
                        @blur="handleGoogleAddressBlur"
                    ></place-auto-complete>

                    <p v-if="!isRefAppSubmitted" class="mt-1 mb-0">
                        <!-- <small>Select an address above to update the fields below.</small> -->
                        <small class="fin-text-body">Search for an address above to update the fields below.</small>
                    </p>
                </section>

                <section v-else class="w-50 text-center" @click="copyAddress">
                    <!-- <el-tag type="info" effect="plain">{{ customFormattedAddress }}</el-tag> -->

                    <el-input
                        :value="customFormattedAddress"
                        class="input-with-select input-disabled"
                        :readonly="true"
                        size="small"
                        v-clipboard:copy="customFormattedAddress"
                        ref="copyAddressInput"
                        :disabled="disabled"
                    >
                        <!-- <div slot="append">
                          <el-button icon="el-icon-scissors" size="small" type="primary">Copy Address</el-button>
                        </div>-->
                    </el-input>
                </section>

                <div>
                    <el-tooltip
                        v-if="!isSearchOpen"
                        class="item"
                        effect="dark"
                        content="Address copied"
                        :value="showAddressCopiedNotice"
                        :manual="true"
                        placement="bottom"
                    >
                        <el-button
                            type="primary"
                            size="small"
                            :plain="!showAddressCopiedNotice"
                            icon="el-icon-scissors"
                            v-clipboard:copy="customFormattedAddress"
                            v-clipboard:success="copyAddress"
                            :class="showAddressCopiedNotice ? 'active' : ''"
                            :disabled="disabled"
                        >
                            Copy Address
                        </el-button>
                    </el-tooltip>

                    <el-button :disabled="disabled" v-if="!isSearchOpen" type="success" size="small" plain
                               @click="showSearch(true)">
                        {{ isAddressEmpty ? "Search" : "Update" }}
                    </el-button>
                </div>
            </div>

            <!-- Address Manual Fields – Bottom Row -->
            <div class="col-12 px-3 pt-3 pb-4">
                <div class="row">
                    <div class="col-6 col-md-1 mb-4 mb-md-3">
                        <application-input class="input-belt" :disabled="isSearchOpen || disabled"
                                           v-model="addressComponents.unit_number">
                            Unit No.
                        </application-input>
                    </div>
                    <div class="col-6 col-md-2 mb-4 mb-md-3">
                        <application-input
                            class="input-belt mb-3"
                            :disabled="isSearchOpen || disabled"
                            v-model="addressComponents.street_number"
                            v-validate="{ id: `${validatePrefix}-street-number` }"
                        >
                            Street No.
                        </application-input>
                    </div>
                    <div class="col-12 col-md-5 mb-4 mb-md-3">
                        <application-input
                            class="input-belt mb-3"
                            :disabled="isSearchOpen || disabled"
                            v-model="addressComponents.route"
                            v-validate="{ id: `${validatePrefix}-street` }"
                        >
                            Street Name
                        </application-input>
                    </div>
                    <div class="col-12 col-md-4 mb-4 mb-md-2">
                        <application-input
                            class="input-belt mb-3"
                            :disabled="isSearchOpen || disabled"
                            v-model="addressComponents.locality"
                            v-validate="{ id: `${validatePrefix}-suburb` }"
                        >
                            Suburb
                        </application-input>
                    </div>
                </div>

                <div class="row">
                    <div class="col-6 col-md-1">
                        <application-input
                            class="input-belt"
                            :disabled="isSearchOpen || disabled"
                            v-model="addressComponents.administrative_area_level_1"
                            v-validate="{ id: `${validatePrefix}-state` }"
                        >
                            State
                        </application-input>
                    </div>

                    <div class="col-6 col-md-2">
                        <application-input
                            class="input-belt"
                            :disabled="isSearchOpen || disabled"
                            v-model="addressComponents.postal_code"
                            v-validate="{ id: `${validatePrefix}-postalcode` }"
                        >
                            Postal Code
                        </application-input>
                    </div>

                    <div class="col-12 col-md-2 offset-md-7-OFF">
                        <application-input
                            class="input-belt"
                            :disabled="isSearchOpen || disabled"
                            v-model="addressComponents.country"
                            v-validate="{ id: `${validatePrefix}-country` }"
                            :maxlength="2"
                        >
                            Country
                        </application-input>
                        <p class="mx-0 mb-0 mt-1">
                            <small>A (2) digit country code.</small>
                        </p>
                    </div>

                    <!-- <div class="col-12 col-md-2 offset-md-5 mb-4 mb-md-2">
                            <label for="">&nbsp;</label>
                            <el-checkbox
                              :id="'addresss-is_current-'+id"
                              v-model="isCurrent"
                              label="Current Address"
                              border
                            />
                    </div>-->
                </div>
            </div>
        </div>
    </section>
</template>

<script>
export default {
    name: "AddressLookup",
    components: {},
    props: {
        title: {
            type: String,
            default: () => "Search for an Address",
        },
        addressId: {
            type: [String, Number],
            default: () => null,
        },
        validatePrefix: {
            type: String,
            default: () => "",
        },
        value: {
            type: Object,
            required: false,
            default: () => ({
                formatted_address: null,
                unit_number: null,
                street_number: null,
                route: null,
                locality: null,
                administrative_area_level_1: null,
                postal_code: null,
                country: null,
            }),
        },
        disabled: {
            type: Boolean,
            required: false,
            default: false
        }
    },
    data() {
        return {
            autoCompleteValue: null,
            isRefAppSubmitted: null,
            searchOpen: false,
            showAddressCopiedNotice: false,
            loading: false,
        }
    },
    computed: {
        addressComponents() {
            return this.value
        },
        isAddressEmpty() {
            const {route, formatted_address} = this.addressComponents
            return !formatted_address || formatted_address.length === 0
        },
        isAddressComponentsEmpty() {
            let address = this.addressComponents
            let checkParameters = ["street_number", "route", "locality", "administrative_area_level_1", "postal_code", "country"]
            let isAddressEmpty = true
            checkParameters.forEach((e) => {
                if (address[e] && address[e].length > 0) isAddressEmpty = false
            })
            return isAddressEmpty
        },
        isSearchOpen() {
            return this.searchOpen || this.isAddressEmpty
        },
        customFormattedAddress() {
            let e = this.addressComponents

            // Set null values to empty string
            Object.keys(e).forEach((key) => {
                if (!e[key]) e[key] = ""
            })

            if (this.isAddressComponentsEmpty) return ""

            const street_number = e.unit_number ? `${e.unit_number}/${e.street_number}` : `${e.street_number}`
            const state = e.administrative_area_level_1
            const address = `${street_number} ${e.route}, ${e.locality} ${state} ${e.postal_code}, ${e.country}`

            // e.g. 3 Boston St, West Croydon SA 5008, Australia
            return address.trim().replace(/ +(?= )/g, "")
        },
    },
    watch: {
        addressComponents: {
            deep: true,
            handler(val) {
                this.autoCompleteValue = _.get(val, 'formatted_address')
                this.$emit("address-changed", this.addressComponents)
                setTimeout(() => this.showSearch(false), 250)
            },
        },
    },
    mounted() {
        this.autoCompleteValue = this.addressComponents.formatted_address
    },
    updated() {
    },
    activated() {
    },
    methods: {
        copyAddress(e) {
            const $container = this.$refs.copyAddressInput.$el
            const $input = $($container).find("input")
            $input.select()
            this.showAddressCopiedNotice = true
            setTimeout(() => (this.showAddressCopiedNotice = false), 1500)
        },
        handleInfoClick(e) {
            e.preventDefault()
        },
        handleGoogleAddressChange(e) {
        },
        handleGoogleAddressBlur(e) {
        },
        showSearch(open) {
            this.searchOpen = open
        },
    },
}
</script>

<style lang="scss" scoped>
// @import '@assets/sass/backend/_core-variables.scss';

.address-lookup__search-container {
    &.animated {
        overflow: hidden;
        transition: height 0.25s linear;

        &.address-lookup__search-container--open {
            height: 62px;
        }

        &.address-lookup__search-container--closed {
            height: 0;
        }
    }
}
</style>

<style lang="scss">
@import "@assets/sass/backend/_core-variables.scss";

.address-lookup {
    .el-input.is-disabled .el-input__inner {
        color: fade-out($color-grey-03, 0.15);
    }
}
</style>
